import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';
import { getMenuAdminDefinition } from '../constants/menuAdminDefinition';

const HOST_API = process.env.REACT_APP_URL;

// La pantalla de permisos nunca se muestra a si misma como opcion: el Perfil Administrador
// no puede reconfigurar sus propios permisos (ver misma restriccion en App.js).
const RESTRINGIDOS = ['permisosMenuPerfilAdministrador'];

const keyDe = (item) => item.to ? item.to.replace(/^\//, '') : null;

const PermisosMenuPerfilAdministrador = () => {
    const toast = useRef(null);
    const { administracionId } = useAuth();
    const t = useTerminologia();

    const definicionMenu = useMemo(() => getMenuAdminDefinition(t), [t]);

    const [ocultos, setOcultos] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (!administracionId) return;
        setLoading(true);
        axios.get(`${HOST_API}api/perfilAdminMenu/GetMenuOcultos`, { params: { administracionId } })
            .then(({ data }) => setOcultos(new Set(data ?? [])))
            .catch(() => {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los permisos actuales', life: 3000 });
                setOcultos(new Set());
            })
            .finally(() => setLoading(false));
    }, [administracionId]);

    const toggleItem = (key) => {
        setOcultos(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    // Un grupo se marca como "todo tildado" / "parcial" en base al estado de sus hojas,
    // y tildar/destildar el grupo propaga el cambio a todas sus hojas.
    const hojasDe = useCallback((item) => {
        const key = keyDe(item);
        if (key) return RESTRINGIDOS.includes(key) ? [] : [key];
        if (item.items) return item.items.flatMap(hojasDe);
        return [];
    }, []);

    const toggleGrupo = (item, marcar) => {
        const claves = hojasDe(item);
        setOcultos(prev => {
            const next = new Set(prev);
            claves.forEach(k => marcar ? next.delete(k) : next.add(k));
            return next;
        });
    };

    const guardar = async () => {
        setGuardando(true);
        try {
            await axios.post(`${HOST_API}api/perfilAdminMenu/SaveMenuOcultos`, {
                administracionId,
                menuKeysOcultos: Array.from(ocultos),
            });
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Permisos guardados correctamente', life: 3000 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudieron guardar los permisos', life: 4000 });
        } finally {
            setGuardando(false);
        }
    };

    const renderNodo = (item, nivel) => {
        const key = keyDe(item);

        if (key) {
            if (RESTRINGIDOS.includes(key)) return null;
            const checked = !ocultos.has(key);
            return (
                <div key={key} className="flex align-items-center gap-2 py-1" style={{ marginLeft: nivel * 24 }}>
                    <Checkbox inputId={`menu-${key}`} checked={checked} onChange={() => toggleItem(key)} />
                    <label htmlFor={`menu-${key}`} style={{ cursor: 'pointer' }}>{item.label}</label>
                </div>
            );
        }

        if (item.items) {
            const claves = hojasDe(item);
            const tildadas = claves.filter(k => !ocultos.has(k));
            const todasTildadas = claves.length > 0 && tildadas.length === claves.length;
            const algunaTildada = tildadas.length > 0 && !todasTildadas;

            return (
                <div key={item.label} style={{ marginLeft: nivel * 24 }} className="mb-2">
                    <div className="flex align-items-center gap-2 py-1">
                        {claves.length > 0 && (
                            <Checkbox
                                checked={todasTildadas}
                                className={algunaTildada ? 'p-checkbox-indeterminate' : ''}
                                onChange={(e) => toggleGrupo(item, e.checked)}
                            />
                        )}
                        <strong>{item.label}</strong>
                    </div>
                    {item.items.map(sub => renderNodo(sub, nivel + 1))}
                </div>
            );
        }

        return null;
    };

    return (
        <>
            <h4>Permisos Perfil Administrador</h4>
            <p style={{ color: '#667085', fontSize: '0.9rem', marginTop: '-0.5rem' }}>
                Elegí qué pantallas puede usar el rol "Perfil Administrador" en esta administradora. Los cambios aplican a todos los usuarios con ese perfil.
            </p>
            <br />
            <Toast ref={toast} />

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="4" />
                </div>
            ) : (
                <>
                    <div className="mb-4">
                        {definicionMenu.map(item => renderNodo(item, 0))}
                    </div>
                    <Button label="Guardar" icon="pi pi-check" onClick={guardar} loading={guardando} />
                </>
            )}
        </>
    );
};

export default PermisosMenuPerfilAdministrador;
